// src/pages/PosPage.tsx — касса (режим кассира)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../api/axios";
import { useScanHandler } from "../context/ScannerContext";
import BarcodeScanner from "../components/BarcodeScanner";
import "./PosPage.css";

interface Prod {
  id: number; nameRu: string; nameKz: string | null; salePrice: number; unit: string; type: string;
  sku: string | null; barcode: string | null; stock: number; exact?: boolean; requireSerial?: boolean;
}
interface Line { p: Prod; qty: number; sn: string[] }
type Method = "cash" | "card" | "kaspi" | "split";
interface PromoInfo { promoId: number; name: string; percent: number; seq: number | null; isPrize: boolean; discount: number }
interface PromoLine { productId: number; qty: number; listPrice: number; price: number; percent: number; promoName: string | null }
interface BonusInfo { enabled: boolean; clientId: number | null; name: string | null; balance: number; maxSpend: number; spent: number; accrual: number; accrualPercent: number; maxPayPercent: number }
interface IssueInfo { promoId: number; name: string; percent: number; days: number; done: boolean; needClient: boolean }
interface PromoCart { lines: PromoLine[]; total: number; promoTotal: number; listTotal: number; discount: number; applied: PromoInfo[]; waiting: PromoInfo[]; issues: IssueInfo[]; bonus: BonusInfo }
interface PromoState { id: number; name: string; percent: number; everyN: number | null; counted: number; nextIsPrize: boolean; untilPrize: number | null; endsAt: string | null }
interface Done { dealId: number; number: string; total: number; receiptUrl: string | null; change: number; receiptError?: string | null; promo?: PromoInfo[]; coupons?: { code: string; percent: number; validUntil: string; promoName: string }[]; bonusSpent?: number; bonusAccrued?: number }
interface Today { count: number; total: number; byMethod: Record<string, number> }

const money = (v: number) => `${Number(v).toLocaleString("ru-RU")} ₸`;
const AUTOPRINT_KEY = "pos.autoprint";

/** Ширина бумаги чековых принтеров 58 мм в драйвере (XP-58: 48,047 мм): другую ширину драйвер молча не печатает */
const ROLL_WIDTH_MM = 48.047;

/** Высота чека в мм: рисуем его в скрытом окне нужной ширины и измеряем */
function measureMm(html: string): Promise<number> {
  return new Promise((resolve) => {
    const f = document.createElement("iframe");
    f.style.cssText = "position:fixed;left:-9999px;top:0;width:200px;height:10px;border:0;visibility:hidden;";
    document.body.appendChild(f);
    const done = (px: number) => { f.remove(); resolve(Math.ceil((px * 25.4) / 96) + 6); };
    f.onload = () => setTimeout(() => done(f.contentDocument?.documentElement?.scrollHeight ?? 0), 150);
    setTimeout(() => done(f.contentDocument?.documentElement?.scrollHeight ?? 700), 4000);
    f.srcdoc = html;
  });
}

/** Печать чека на ленту 57/58 мм: в настольном приложении — молча на принтер из настроек, в браузере — через скрытый iframe */
async function printReceipt(dealId: number) {
  const res = await api.get(`/pos/receipt/${dealId}`, { params: { autoprint: 0 }, responseType: "text", transformResponse: (d) => d });
  const desk = (window as any).keyoffDesktop;
  if (desk?.printHtml) {
    // Чек печатается на «Чековую ленту» из настроек приложения: рулон точной ширины, высота по содержимому.
    const hMm = Math.max(30, await measureMm(res.data as string));
    const r = await desk.printHtml({ html: res.data as string, kind: "receipt", sizeMm: { w: ROLL_WIDTH_MM, h: hMm } });
    if (!r?.ok) alert("Чек не напечатан: " + (r?.reason || "ошибка принтера"));
    return;
  }
  const frame = document.createElement("iframe");
  frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;";
  document.body.appendChild(frame);
  frame.srcdoc = res.data as string;
  frame.onload = () => {
    try { frame.contentWindow?.focus(); frame.contentWindow?.print(); } catch { /* печать недоступна */ }
    setTimeout(() => frame.remove(), 60000);
  };
}

export default function PosPage({ onSold }: { onSold?: () => void }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState<Prod[]>([]);
  const [camOpen, setCamOpen] = useState(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [method, setMethod] = useState<Method>("cash");
  const [received, setReceived] = useState("");
  const [splitCash, setSplitCash] = useState("");
  const [splitOther, setSplitOther] = useState<"card" | "kaspi">("card");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ ok: boolean; text: string } | null>(null);
  const [flashId, setFlashId] = useState<number | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [autoPrint, setAutoPrint] = useState(() => localStorage.getItem(AUTOPRINT_KEY) !== "0");
  const [promo, setPromo] = useState<PromoCart | null>(null);
  const [promoKey, setPromoKey] = useState("");
  const [bonusWant, setBonusWant] = useState("");
  const [promoStatus, setPromoStatus] = useState<PromoState[]>([]);
  const requestId = useRef<string>("");
  const searchRef = useRef<HTMLInputElement>(null);
  const linesRef = useRef<HTMLDivElement>(null);
  const prevLines = useRef(0);
  // новая позиция — докручиваем список, чтобы она была на виду
  useEffect(() => {
    if (lines.length > prevLines.current) { const el = linesRef.current; if (el) el.scrollTop = el.scrollHeight; }
    prevLines.current = lines.length;
  }, [lines.length]);

  const newRequestId = () => { requestId.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; };
  useEffect(() => { newRequestId(); }, []);
  useEffect(() => { try { localStorage.setItem(AUTOPRINT_KEY, autoPrint ? "1" : "0"); } catch { /* */ } }, [autoPrint]);

  // ── поиск ──
  useEffect(() => {
    const term = q.trim();
    if (!term) { setResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.get("/pos/search", { params: { q: term } }).then((r) => { if (!cancelled) setResults(r.data); }).catch(() => { if (!cancelled) setResults([]); });
    }, 180);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  useEffect(() => { if (!flash) return; const t = setTimeout(() => setFlash(null), 4000); return () => clearTimeout(t); }, [flash]);

  const inCart = (id: number) => lines.find((l) => l.p.id === id)?.qty ?? 0;
  const canAdd = (p: Prod) => p.type !== "product" || inCart(p.id) < p.stock;

  const add = (p: Prod) => {
    if (!canAdd(p)) { setFlash({ ok: false, text: `«${p.nameRu}»: на складе только ${p.stock}` }); return false; }
    setLines((prev) => {
      const i = prev.findIndex((l) => l.p.id === p.id);
      if (i >= 0) { const n = prev.slice(); n[i] = { p: n[i].p, qty: n[i].qty + 1, sn: [...n[i].sn, ""] }; return n; }
      return [...prev, { p, qty: 1, sn: [""] }];
    });
    if (p.requireSerial) setTimeout(focusNextSn, 80);
    setFlashId(p.id);
    setTimeout(() => setFlashId((c) => (c === p.id ? null : c)), 800);
    return true;
  };

  const onEnter = async (override?: string) => {
    const term = (override ?? q).trim();
    if (!term) return;
    try {
      const r = await api.get("/pos/search", { params: { q: term } });
      const list: Prod[] = r.data;
      const hit = list.find((x) => x.exact) || (list.length === 1 ? list[0] : null);
      if (hit) {
        if (add(hit)) setFlash({ ok: true, text: `${hit.nameRu} — ${money(hit.salePrice)}` });
        setQ(""); setResults([]);
      } else {
        setResults(list);
        if (!list.length) setFlash({ ok: false, text: `Ничего не найдено по «${term}»` });
      }
    } catch { setFlash({ ok: false, text: "Ошибка поиска" }); }
    searchRef.current?.focus();
  };

  const setQty = (id: number, n: number) =>
    setLines((prev) => prev.map((l) => {
      if (l.p.id !== id) return l;
      const max = l.p.type === "product" ? l.p.stock : 999;
      const qty = Math.max(1, Math.min(max, n || 1));
      return { p: l.p, qty, sn: Array.from({ length: qty }, (_, i) => l.sn[i] ?? "") };
    }));
  const remove = (id: number) => setLines((prev) => prev.filter((l) => l.p.id !== id));
  const setSn = (id: number, i: number, v: string) =>
    setLines((prev) => prev.map((l) => (l.p.id === id ? { ...l, sn: l.sn.map((x, k) => (k === i ? v.replace(/\s+/g, "") : x)) } : l)));
  // курсор на первый пустой серийный номер; если все заполнены — обратно в поиск
  const focusNextSn = () => {
    const el = Array.from(document.querySelectorAll<HTMLInputElement>("input[data-sn]")).find((x) => !x.value.trim());
    if (el) el.focus(); else searchRef.current?.focus();
  };

  // ── итоги ──
  const plainTotal = useMemo(() => Math.round(lines.reduce((s, l) => s + l.p.salePrice * l.qty, 0) * 100) / 100, [lines]);
  // цены с учётом акций приходят с сервера; пока расчёт не готов, оплату не даём
  // ключ расчёта: состав чека + телефон покупателя (купоны и бонусы) + сколько бонусов тратим
  const phoneKey = phone.replace(/\D/g, "").length >= 10 ? phone.replace(/\D/g, "") : "";
  const linesKey = useMemo(() => lines.map((l) => `${l.p.id}x${l.qty}`).join(",") + `|${phoneKey}|${bonusWant}`, [lines, phoneKey, bonusWant]);
  const promoReady = lines.length === 0 || (promo !== null && promoKey === linesKey);
  const total = promoReady && promo && lines.length > 0 ? promo.total : plainTotal;

  useEffect(() => {
    if (!lines.length) { setPromo(null); setPromoKey(""); return; }
    let cancelled = false;
    const key = linesKey;
    const t = setTimeout(() => {
      api.post("/pos/promo/preview", { items: lines.map((l) => ({ productId: l.p.id, quantity: l.qty })), phone: phoneKey || undefined, bonus: Number(bonusWant) || undefined })
        .then((r) => { if (!cancelled) { setPromo(r.data); setPromoKey(key); } })
        .catch(() => { if (!cancelled) { setPromo(null); setPromoKey(key); } });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linesKey]);

  const loadPromoStatus = useCallback(() => { api.get("/pos/promo/status").then((r) => setPromoStatus(r.data)).catch(() => setPromoStatus([])); }, []);
  useEffect(() => { loadPromoStatus(); }, [loadPromoStatus]);
  const recvNum = Number(received.replace(",", ".")) || 0;
  const change = method === "cash" && recvNum > total ? Math.round((recvNum - total) * 100) / 100 : 0;
  const splitCashNum = Math.min(Math.max(Number(splitCash.replace(",", ".")) || 0, 0), total);
  const splitRest = Math.round((total - splitCashNum) * 100) / 100;

  const serialsOk = lines.every((l) => !l.p.requireSerial || (l.sn.length === l.qty && l.sn.every((s) => s.trim())));
  const canPay =
    lines.length > 0 && total > 0 && !busy && promoReady && serialsOk &&
    (method !== "cash" || received === "" || recvNum >= total) &&
    (method !== "split" || (splitCashNum > 0 && splitRest > 0));

  const reset = useCallback(() => {
    setLines([]); setQ(""); setResults([]); setReceived(""); setSplitCash(""); setPhone(""); setName(""); setBonusWant(""); setMethod("cash");
    setDone(null); newRequestId();
    setTimeout(() => searchRef.current?.focus(), 50);
  }, []);

  const pay = async () => {
    if (!canPay) return;
    setBusy(true); setFlash(null);
    try {
      const payments = method === "split"
        ? [{ method: "cash", amount: splitCashNum }, { method: splitOther, amount: splitRest }]
        : [{ method, amount: total }];
      const cashPart = method === "cash" ? total : method === "split" ? splitCashNum : 0;
      const res = await api.post("/pos/sale", {
        requestId: requestId.current,
        items: lines.map((l) => ({ productId: l.p.id, quantity: l.qty, serials: l.p.requireSerial ? l.sn.map((s) => s.trim()) : undefined })),
        payments,
        received: cashPart > 0 && (method === "split" ? false : recvNum > 0) ? recvNum : undefined,
        clientPhone: phone.trim() || undefined,
        bonusSpend: promoReady && promo && promo.bonus.spent > 0 ? Number(bonusWant) : undefined,
        clientName: name.trim() || undefined,
      });
      const d: Done = { ...res.data, change };
      setDone(d);
      loadPromoStatus();
      onSold?.();
      if (autoPrint) printReceipt(d.dealId).catch((e) => alert("Чек не напечатан: " + (e?.message || "ошибка")));
    } catch (e: any) {
      const msg = e?.response?.data?.message;
      setFlash({ ok: false, text: Array.isArray(msg) ? msg.join("\n") : msg || "Не удалось оформить продажу" });
    } finally { setBusy(false); }
  };

  // код с телефона: если курсор в поле серийного номера, он уходит туда, иначе ищем товар по штрихкоду
  const handleScan = (code: string) => {
    if (done) return;
    const a = document.activeElement as HTMLInputElement | null;
    if (a && a.hasAttribute("data-sn")) {
      setSn(Number(a.getAttribute("data-sn")), Number(a.getAttribute("data-i")), code);
      setTimeout(focusNextSn, 40);
    } else {
      onEnter(code);
    }
  };
  useScanHandler(handleScan);

  // горячие клавиши: F2 — поиск, F9 — оплатить
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (done) { if (e.key === "Enter") { e.preventDefault(); reset(); } return; }
      if (e.key === "F2") { e.preventDefault(); searchRef.current?.focus(); }
      if (e.key === "F9") { e.preventDefault(); pay(); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const quick = [total, 5000, 10000, 20000, 50000].filter((v, i, a) => v > 0 && v >= total && a.indexOf(v) === i).slice(0, 4);

  return (
    <div className="ps-page ps-pos">
      {/* ЛЕВО: поиск */}
      <div className="ps-left">
        <div className="ps-search">
          <span>🔍</span>
          <input
            ref={searchRef}
            className="ps-input ps-big"
            autoFocus
            placeholder="Штрихкод, SKU или название  (F2)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); onEnter(); } }}
          />
          <button type="button" className="ps-cam" title="Сканировать штрихкод камерой" aria-label="Сканировать камерой" onClick={() => setCamOpen(true)}>📷</button>
        </div>
        {camOpen && (
          <BarcodeScanner
            continuous
            title="Сканируйте товары: код сразу попадает в чек"
            closeLabel="Готово"
            onClose={() => { setCamOpen(false); setTimeout(() => searchRef.current?.focus(), 50); }}
            onDetected={handleScan}
            footer={
              <div>
                {flash && <div className={`ps-flash ${flash.ok ? "" : "err"}`}>{flash.text}</div>}
                <div className="ps-hint" style={{ color: "#9ca3af", marginTop: 4 }}>В чеке: {lines.reduce((s, l) => s + l.qty, 0)} шт. на {money(plainTotal)}</div>
              </div>
            }
          />
        )}
        {flash && <div className={`ps-flash ${flash.ok ? "" : "err"}`}>{flash.text}</div>}
        <div className="ps-results">
          {q.trim() ? (
            results.length === 0 ? <div className="ps-hint">Нет совпадений</div> :
              results.map((p) => {
                const off = !canAdd(p);
                return (
                  <div key={p.id} className={`ps-res ${p.exact ? "exact" : ""} ${off ? "off" : ""}`} onClick={() => { if (add(p)) { setQ(""); setResults([]); searchRef.current?.focus(); } else searchRef.current?.focus(); }}>
                    <div className="ps-res-main">
                      <div className="ps-res-name">{p.nameRu}</div>
                      <div className="ps-res-sub">{[p.sku && `SKU ${p.sku}`, p.barcode && `ШК ${p.barcode}`].filter(Boolean).join(" · ") || "без артикула"}</div>
                    </div>
                    <span className={`ps-stock ${p.type !== "product" ? "svc" : p.stock <= 0 ? "zero" : ""}`}>
                      {p.type !== "product" ? "услуга/ПО" : p.stock <= 0 ? "нет" : `${p.stock} ${p.unit || "шт"}`}
                    </span>
                    <div className="ps-res-price">{money(p.salePrice)}</div>
                  </div>
                );
              })
          ) : (
            <div className="ps-empty"><b>📦</b>Отсканируйте штрихкод или введите название.<br />Enter добавляет товар в чек, повторный скан увеличивает количество.</div>
          )}
        </div>
      </div>

      {/* ПРАВО: чек и оплата */}
      <div className="ps-cart">
        <div className="ps-card ps-cart-card">
          {promoStatus.filter((s) => s.everyN).map((s) => (
            <div key={s.id} className={`ps-promo-banner ${s.nextIsPrize ? "prize" : ""}`}>
              {s.nextIsPrize
                ? <>🎁 <b>Следующий чек призовой</b> по акции «{s.name}»: −{s.percent}%</>
                : <>🎁 «{s.name}»: до призового чека ещё {s.untilPrize}</>}
            </div>
          ))}
          <div className="ps-h">Чек {lines.length > 0 && <button className="ps-btn small ghost" style={{ marginLeft: "auto", textTransform: "none", letterSpacing: 0 }} onClick={reset}>Очистить</button>}</div>
          <div className="ps-lines" ref={linesRef}>
            {lines.length === 0 ? <div className="ps-empty" style={{ padding: 16 }}>Чек пуст</div> : lines.map(({ p, qty, sn }, idx) => {
              const pl = promoReady && promo && promo.lines[idx]?.productId === p.id ? promo.lines[idx] : null;
              const off = !!pl && pl.percent > 0 && pl.price < pl.listPrice;
              return (
              <React.Fragment key={p.id}>
              <div className={`ps-line ${flashId === p.id ? "flash" : ""}`}>
                <div className="ps-line-main">
                  <div className="ps-line-name">{p.nameRu}</div>
                  <div className="ps-line-sub">
                    {off ? <><s className="ps-old">{money(p.salePrice)}</s> <span className="ps-promo-price">{money(pl!.price)}</span> <span className="ps-promo-tag">🎁 −{pl!.percent}%</span></> : money(p.salePrice)}
                    {p.type === "product" && qty >= p.stock ? " · последние на складе" : ""}
                  </div>
                </div>
                <div className="ps-step">
                  <button onClick={() => setQty(p.id, qty - 1)} aria-label="Меньше">−</button>
                  <input
                    inputMode="numeric"
                    aria-label="Количество"
                    value={qty}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => setQty(p.id, parseInt(e.target.value.replace(/D/g, ""), 10))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchRef.current?.focus(); } }}
                  />
                  <button onClick={() => setQty(p.id, qty + 1)} aria-label="Больше">+</button>
                </div>
                <div className="ps-line-sum">{money((off ? pl!.price : p.salePrice) * qty)}</div>
                <button className="ps-x" onClick={() => remove(p.id)} title="Убрать">×</button>
              </div>
              {p.requireSerial && (
                <div className="ps-sn">
                  <div className="ps-sn-lab">Серийные номера: отсканируйте или введите для каждой единицы</div>
                  {sn.map((v, i) => (
                    <input
                      key={i}
                      className={`ps-sn-in ${v.trim() ? "" : "need"}`}
                      data-sn={p.id}
                      data-i={i}
                      placeholder={`S/N ${i + 1}`}
                      value={v}
                      onChange={(e) => setSn(p.id, i, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); focusNextSn(); } }}
                    />
                  ))}
                </div>
              )}
              </React.Fragment>
              );
            })}
          </div>
          {promoReady && promo && promo.discount > 0 && (
            <div className="ps-promo-row"><span>🎁 {promo.applied.map((a) => a.seq && a.seq > 0 ? `${a.name} (чек №${a.seq})` : a.name).join(", ")}</span><b>−{money(promo.discount)}</b></div>
          )}
          {promoReady && promo && promo.bonus.spent > 0 && (
            <div className="ps-promo-row"><span>🪙 Оплачено бонусами</span><b>−{money(promo.bonus.spent)}</b></div>
          )}
          {promoReady && promo && (promo.issues ?? []).filter((i) => !i.done).map((i) => (
            <div key={i.promoId} className="ps-promo-banner" style={{ marginTop: 8 }}>
              🎟 За эту покупку покупатель получит купон −{i.percent}% на {i.days} дн.
              {i.needClient && <b style={{ color: "#fbbf24" }}> Введите телефон покупателя, иначе купон не выдастся.</b>}
            </div>
          ))}
          {!serialsOk && <div className="ps-warn" style={{ margin: "8px 0" }}>Введите серийные номера всех товаров с отметкой S/N</div>}
          <div className="ps-total"><span>К оплате</span><b>{money(total)}</b></div>
        </div>

        <div className="ps-card ps-pay">
          <div className="ps-methods">
            <button className={`ps-btn ${method === "cash" ? "on" : ""}`} onClick={() => setMethod("cash")}><i>💵</i>Наличные</button>
            <button className={`ps-btn ${method === "card" ? "on" : ""}`} onClick={() => setMethod("card")}><i>💳</i>Карта</button>
            <button className={`ps-btn ${method === "kaspi" ? "on" : ""}`} onClick={() => setMethod("kaspi")}><i>📱</i>Kaspi</button>
            <button className={`ps-btn ${method === "split" ? "on" : ""}`} onClick={() => setMethod("split")}><i>➗</i>Смешанная</button>
          </div>

          {method === "cash" && (
            <>
              <div className="ps-cashrow">
                <span className="ps-hint">Получено</span>
                <input className="ps-input" inputMode="decimal" placeholder={String(total || "")} value={received} onChange={(e) => setReceived(e.target.value.replace(/[^\d.,]/g, ""))} />
                <div className="ps-quick">{quick.map((v) => <button key={v} className="ps-btn small" onClick={() => setReceived(String(v))}>{v === total ? "Без сдачи" : v.toLocaleString("ru-RU")}</button>)}</div>
              </div>
              {received !== "" && <div className={`ps-change ${recvNum >= total ? "ok" : "bad"}`}>{recvNum >= total ? `Сдача: ${money(change)}` : `Не хватает: ${money(total - recvNum)}`}</div>}
            </>
          )}
          {method === "split" && (
            <div className="ps-split">
              <label className="ps-field">Наличными<input className="ps-input" inputMode="decimal" value={splitCash} onChange={(e) => setSplitCash(e.target.value.replace(/[^\d.,]/g, ""))} /></label>
              <label className="ps-field">Остаток ({money(splitRest)})
                <select className="ps-input" value={splitOther} onChange={(e) => setSplitOther(e.target.value as any)}><option value="card">Картой</option><option value="kaspi">Kaspi</option></select>
              </label>
            </div>
          )}

          <details className="ps-details">
            <summary>Покупатель (бонусы и купоны)</summary>
            <div className="ps-split">
              <input className="ps-input" placeholder="Телефон +7…" inputMode="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setBonusWant(""); }} />
              <input className="ps-input" placeholder="Имя" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
          </details>
          {promoReady && promo && promo.bonus.enabled && lines.length > 0 && (
            <div className="ps-bonus">
              {promo.bonus.clientId ? (
                <>
                  <div>🪙 {promo.bonus.name || "Покупатель"}: бонусов <b>{money(promo.bonus.balance)}</b></div>
                  {promo.bonus.maxSpend > 0 ? (
                    <div className="ps-bonus-row">
                      <input className="ps-input" inputMode="numeric" placeholder={`списать до ${promo.bonus.maxSpend}`} value={bonusWant} onChange={(e) => setBonusWant(e.target.value.replace(/\D/g, ""))} />
                      <button className="ps-btn small" onClick={() => setBonusWant(String(promo.bonus.maxSpend))}>Максимум</button>
                      {bonusWant && <button className="ps-btn small ghost" onClick={() => setBonusWant("")}>Сброс</button>}
                    </div>
                  ) : <div className="ps-hint">Списать пока нечего (лимит {promo.bonus.maxPayPercent}% чека)</div>}
                </>
              ) : phoneKey
                ? <div className="ps-hint">Новый покупатель: бонусы начнут копиться с этой покупки</div>
                : <div className="ps-hint">Введите телефон покупателя, чтобы начислить и списать бонусы</div>}
              {promo.bonus.accrual > 0 && <div className="ps-hint">За эту покупку начислим {money(promo.bonus.accrual)} бонусов ({promo.bonus.accrualPercent}%)</div>}
            </div>
          )}
          <label className="ps-check"><input type="checkbox" checked={autoPrint} onChange={(e) => setAutoPrint(e.target.checked)} />Печатать чек сразу после оплаты</label>
          <button className="ps-btn pay" disabled={!canPay} onClick={pay}>{busy ? "Оформляю…" : `Оплатить ${money(total)}  (F9)`}</button>
        </div>
      </div>

      {done && (
        <div className="ps-overlay">
          <div className="ps-card ps-done">
            <div className="ps-ok">✅</div>
            <h2>Продажа оформлена</h2>
            <div className="ps-muted">{done.number}</div>
            <div className="ps-big-sum">{money(done.total)}</div>
            {done.promo && done.promo.length > 0 && (
              <div className="ps-promo-banner prize" style={{ textAlign: "center" }}>
                🎁 {done.promo.map((a) => `${a.name}${a.seq ? ` — призовой чек №${a.seq}` : ""}: скидка ${money(a.discount)}`).join("; ")}
              </div>
            )}
            {done.change > 0 && <div className="ps-change ok">Сдача: {money(done.change)}</div>}
            {(done.bonusSpent ?? 0) > 0 && <div className="ps-hint">🪙 Оплачено бонусами: {money(done.bonusSpent!)}</div>}
            {(done.bonusAccrued ?? 0) > 0 && <div className="ps-hint">🪙 Начислено бонусов: {money(done.bonusAccrued!)}</div>}
            {(done.coupons ?? []).map((c) => (
              <div key={c.code} className="ps-promo-banner prize" style={{ textAlign: "center" }}>
                🎟 Выдан купон <b style={{ letterSpacing: 1 }}>{c.code}</b>: −{c.percent}% до {c.validUntil.split("-").reverse().join(".")} (напечатан на чеке)
              </div>
            ))}
            {done.receiptUrl
              ? <div className="ps-hint">Фискальный чек пробит</div>
              : <div className="ps-warn">Фискальный чек пока не получен{done.receiptError ? `: ${done.receiptError}` : ""}. Сообщите руководителю.</div>}
            <div className="ps-row">
              <button className="ps-btn" onClick={() => printReceipt(done.dealId).catch(() => alert("Не удалось подготовить чек"))}>🖨 Печать чека</button>
              <button className="ps-btn primary" autoFocus onClick={reset}>Новая продажа (Enter)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Оболочка кассира: без меню CRM, только шапка с итогами смены и касса */
export function CashierShell({ user, onLogout }: { user: any; onLogout: () => void }) {
  const [today, setToday] = useState<Today | null>(null);
  const load = useCallback(() => { api.get("/pos/today").then((r) => setToday(r.data)).catch(() => undefined); }, []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  return (
    <div className="ps-shell ps-page">
      <div className="ps-bar">
        <img src="/logo192.png" alt="" /><b>Касса</b>
        <span className="ps-muted">{[user.lastName, user.firstName].filter(Boolean).join(" ")}</span>
        <div className="ps-stat">
          <span>Чеков сегодня: <strong>{today?.count ?? 0}</strong></span>
          <span>Выручка: <strong>{money(today?.total ?? 0)}</strong></span>
          <span>Нал: <strong>{money(today?.byMethod?.cash ?? 0)}</strong></span>
          <button className="ps-btn small" onClick={onLogout}>Выйти</button>
        </div>
      </div>
      <div className="ps-body"><PosPage onSold={load} /></div>
    </div>
  );
}
